import {
	CupResponse,
	CupStatus,
	IAnalyteInCup,
} from "../../domain/cup.interface";
import { CupRequest } from "../../services/cup.service";

/** A vendor already in use, as `GET /cups/vendors` returns it (SIMS-8962 AC2). */
export interface VendorInUse {
	vendor: string;
	cupCount: number;
}

/** What the editor holds while it is open. Ids for analytes, matching the create payload. */
export interface CupDraft {
	// `useConfigEditor<T extends Record<string, unknown>>` compares drafts structurally, so the
	// shape has to be indexable. Payers declares its draft as a type alias for the same reason.
	[key: string]: unknown;
	name: string;
	vendor: string;
	display: string;
	description: string;
	status: CupStatus;
	analytes: string[];
}

export const emptyCupDraft = (): CupDraft => ({
	name: "",
	vendor: "",
	display: "",
	description: "",
	status: CupStatus.ACTIVE,
	analytes: [],
});

export const analyteIds = (cup: CupResponse): string[] =>
	Array.isArray(cup.analytes)
		? (cup.analytes as (string | IAnalyteInCup)[]).map((a) =>
				typeof a === "string" ? a : a._id,
			)
		: [];

export const cupDraftFrom = (cup: CupResponse): CupDraft => ({
	name: cup.name ?? "",
	vendor: cup.vendor ?? "",
	// A display equal to the vendor is treated as NOT SET.
	//
	// The server resolves the fallback on every read — `getAccountCups` ends with
	// `cups.map((cup) => this.resolveDisplay(cup))`, and `resolveDisplay` returns
	// `{ ...cup, display: cup.vendor }` when the stored value is blank. So the editor NEVER sees
	// the stored empty value; it sees the vendor. Without this, reopening a cup pre-fills the
	// field with "CLIA", the user can no longer reach the blank state AC5 is about, and the next
	// save writes the vendor into storage through the PUT passthrough — laundering in exactly what
	// SIMS-8962 AC6 was amended to keep out, and what C17933 asserts against in the database.
	//
	// The one case this loses is a user who genuinely typed the vendor as the display; that
	// resolves identically on read, so nothing is visibly different.
	display:
		(cup.display ?? "") === (cup.vendor ?? "") ? "" : (cup.display ?? ""),
	description: cup.description ?? "",
	status: cup.status ?? CupStatus.ACTIVE,
	analytes: analyteIds(cup),
});

/** AC1 — name and vendor are the only required fields; description is optional, as in the old cup form (Q5). */
export const canSaveCup = (draft: CupDraft): boolean =>
	draft.name.trim().length > 0 && draft.vendor.trim().length > 0;

/**
 * AC5 — what gets sent.
 *
 * `display` is sent EXACTLY as typed, trimmed, and **empty when blank**. The editor never
 * substitutes the vendor.
 *
 * The Hi-Fi does the opposite: `commitCup` persists `vendorDisplay: d.vendorDisplay || d.vendor`
 * in both branches (markup 1168/1170), and picking a vendor suggestion writes it eagerly (1384).
 * SIMS-8962 AC6 shipped a READ-TIME fallback instead, and was amended specifically to forbid
 * storing it — a later vendor rename would leave a stale copy behind — with TestRail C17933
 * asserting the empty storage at the database level. Copying the prototype here would fail that
 * case and undo shipped behaviour. Gate 1 Q1.
 *
 * Trimming matters on its own: `d.vendorDisplay || d.vendor` treats "   " as truthy, so the Hi-Fi
 * persists whitespace and silently defeats its own default.
 */
export const toCupPayload = (
	draft: CupDraft,
	accountId: string,
	createdBy: string,
): CupRequest => ({
	accountId,
	createdBy,
	name: draft.name.trim(),
	vendor: draft.vendor.trim(),
	display: draft.display.trim(),
	description: draft.description.trim(),
	status: draft.status,
	analytes: draft.analytes,
});

/** AC5 — the placeholder states the fallback without performing it. */
export const vendorDisplayPlaceholder = (draft: CupDraft): string =>
	draft.vendor.trim() ? draft.vendor.trim() : "Same as vendor";

/**
 * AC2 — vendors already in use, narrowed by what has been typed.
 *
 * Substring, case-insensitive, so typing "CLIA" surfaces "CLIAWaived" — which is the point of the
 * AC: a near-duplicate spelling shows up BEFORE it is created, because each new spelling becomes
 * its own filter entry on the list.
 */
export const filterVendorSuggestions = (
	query: string,
	vendors: VendorInUse[],
): VendorInUse[] => {
	const q = query.trim().toLowerCase();
	return q
		? vendors.filter((v) => v.vendor.toLowerCase().includes(q))
		: vendors;
};

export const vendorCountLabel = (v: VendorInUse): string =>
	`${v.cupCount} ${v.cupCount === 1 ? "cup" : "cups"}`;

/**
 * AC3 — the duplicate-name refusal, which is the SERVER's.
 *
 * SIMS-8962 AC4 enforces it with a partial unique index on (accountId, vendor, normalizedName),
 * case-insensitive and whitespace-trimmed, and answers 409. The editor surfaces that and nothing
 * more: a client-side check would need every cup in the account and would get the normalisation
 * rule wrong, and AC4 requires the same name to be ACCEPTED under a different vendor.
 */
export const duplicateNameMessage = (error: unknown): string | null => {
	// TWO SHAPES, and the one that actually arrives is the first. `cup.service.ts` rejects with
	// `err.response?.data || err`, so what reaches here is the response BODY — `{ statusCode: 409,
	// error, message }` — not an axios error. The unit tests originally only covered the axios
	// shape, which was invented rather than read off the service, and the E2E caught it.
	const e = error as {
		statusCode?: number;
		message?: unknown;
		response?: { status?: number; data?: { message?: unknown } };
	};
	const status = e?.statusCode ?? e?.response?.status;
	if (status !== 409) {
		return null;
	}
	// WHICH message, and it is not interchangeable. The service rejects with
	// `err.response?.data || err`, so when the 409 carries no body the RAW AXIOS ERROR
	// arrives — and its `.message` is axios's own "Request failed with status code 409",
	// which would be shown to the user as the reason the save was refused. Presence of
	// `.response` is what distinguishes the two shapes: an unwrapped body has none.
	const message = e?.response ? e.response.data?.message : e?.message;
	return typeof message === "string" && message.trim()
		? message
		: "That cup name is already in use for this vendor.";
};
